import { ChartBar } from "lucide-react";
import type React from "react";
import { useGetUsage } from "@/app/billing/api/hooks";
import { ChartCard } from "@/app/dashboard/widgets/cards/chart-card";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useUsageCharts } from "../../hooks/use-usage-charts";

type UsageChartsProps = {
  className?: string;
  showEmptyState?: boolean;
  fallback?: React.ReactNode;
};

export const UsageCharts: React.FC<UsageChartsProps> = ({
  className = "grid-cols-1 md:grid-cols-2",
  showEmptyState = true,
  fallback = null,
}) => {
  const { organizationId } = useOrgProjectRouteContext();
  const { data: usageData } = useGetUsage(organizationId);
  const chartConfigs = useUsageCharts(usageData);

  const hasAnyUsage =
    usageData?.length &&
    chartConfigs.some((chart) =>
      chart.data.some((point) => Number(point[chart.dataKey]) > 0)
    );

  // If there is no usage data, render fallback or empty state
  if (!hasAnyUsage) {
    if (!showEmptyState) {
      return fallback;
    }
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border bg-card">
        <div className="p-4 text-center">
          <ChartBar className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 text-muted-foreground text-sm">No usage yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid ${className} gap-6`}>
      {chartConfigs.map((chart, index) => (
        <ChartCard
          chartType={chart.chartType}
          colorIndex={index + 1}
          data={chart.data}
          dataKey={chart.dataKey}
          description={chart.description}
          key={`${chart.title}-${chart.dataKey}`}
          title={chart.title}
          usageMetric={chart.usageMetric}
        />
      ))}
    </div>
  );
};
